import React, { useState, useEffect, useMemo } from 'react';

import { makeStyles } from '@material-ui/core/styles';

import Grid from '@material-ui/core/Grid';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Switch from '@material-ui/core/Switch';
import Paper from '@material-ui/core/Paper';

import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import ListItemIcon from '@material-ui/core/ListItemIcon';
import ListItemSecondaryAction from '@material-ui/core/ListItemSecondaryAction';
import Checkbox from '@material-ui/core/Checkbox';

import SaveButton from 'app/components/buttons/SaveButton';
import Loading from 'app/components/loaders/Loading';

import { paper } from 'app/styles/core';

import useApi from 'app/context/useApi';

const useListItemStyles = makeStyles(theme => ({
    nested: props => ({
        paddingLeft: (10 + props.depth * 14),
    }),
    listItemIcon: {
        minWidth: 0
    }
}));

const findChildIds = (children) => {

    let ids = [];

    children.forEach(c => {

        ids.push(c.id);

        if (c.children_with_count.length > 0) {
            ids = ids.concat(findChildIds(c.children_with_count));
        }
    })

    return ids;
}

const NestedLevelItem = (props) => {

    const { level, depth, levelsChecked, onCheck, onCheckChildren } = props;

    const classes = useListItemStyles({ depth: depth});

    const [ includeChildren, setIncludeChildren ] = useState(false);

    const isSelected = levelsChecked.indexOf(level.id) > -1 ;

    const childIds = (level.children_with_count.length === 0) ? 0 : useMemo(() => findChildIds(level.children_with_count), []);

    const handleIncludeChildren = () => {
        setIncludeChildren(! includeChildren);
        const ids = [...childIds];
        if (! includeChildren) ids.push(level.id);
        onCheckChildren(! includeChildren, ids);
    }

    return (
        <>
            <ListItem
                classes={{ root: classes.nested }}
                button
                dense
                onClick={() => onCheck(level.id)}
                >
                <ListItemIcon classes={{ root: classes.listItemIcon }}>
                    <Checkbox
                        edge="start"
                        checked={isSelected}
                        tabIndex={-1}
                        disableRipple
                        color="primary"
                    />
                </ListItemIcon>
                <ListItemText
                    primary={ level.title }
                    className={ classes.listItemText }
                />
                <ListItemSecondaryAction>
                    <Typography
                        display="inline"
                        color="textSecondary"
                        variant="caption"
                        >
                        Incl. Sub
                    </Typography>
                    <Switch
                        edge="end"
                        onChange={handleIncludeChildren}
                        checked={includeChildren}
                        disabled={level.children_with_count.length === 0}
                        color="primary"
                    />
                </ListItemSecondaryAction>
            </ListItem>
            {level.children_with_count.map((child) => (
                <NestedLevelItem
                    key={child.id}
                    level={child}
                    depth={(depth + 1)}
                    levelsChecked={levelsChecked}
                    onCheck={onCheck}
                    onCheckChildren={onCheckChildren}
                />
            ))}
        </>
    )
}

const useStyles = makeStyles(theme => ({
    paper: {
        ...paper(theme),
    },
    relative: {
        position: 'relative'
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    saveButtonContainer: {
        marginTop: theme.spacing(3)
    },
    saveButton: {
        paddingLeft: theme.spacing(5),
        paddingRight: theme.spacing(5),
    },
    scroll: {
        maxHeight: 400,
        overflowY: 'scroll',
        borderRadius: theme.shape.borderRadius,
        border: `1px solid ${theme.palette.grey[400]}`
    },
    noContentPadding: {
        padding: 0
    },
    listItemIcon: {
        minWidth: 0
    },
    listItem: {
        paddingLeft: 24
    }
}));

const initOnboardingSettings = {
    onboard_message: "",
    onboard_profile: false,
    onboard_password: false,
    onboard_membership: false,
    onboard_membership_invoice: false,
}

export default function General(props) {

    const api = useApi();

    const [ onboardingSettings, setOnboardingSettings ] = useState(initOnboardingSettings);

    const [ groupsChecked, setGroupsChecked ] = useState([]);

    const [ groups, setGroups ] = useState([]);
    const [ levels, setLevels ] = useState([]);

    const [ groupsLoading, setGroupsLoading ] = useState(false);
    const [ levelsLoading, setLevelsLoading ] = useState(false);
    const [ loading, setLoading ] = useState(false);
    const [ errors, setErrors ] = useState({});

    const classes = useStyles();

    const getGroups = () => {
        setGroupsLoading(true);

        api.get('/admin/group/group')
            .then(res => setGroups(res.groups))
            .finally(() => setGroupsLoading(false));
    }

    const getLevels = () => {
        setLevelsLoading(true);

        api.get('/admin/group/level/recursive/0')
            .then(res => setLevels(res.groups))
            .finally(() => setLevelsLoading(false));
    }

    useEffect(() => {
        getGroups();
        getLevels();
    }, []);

    const handleChange = name => event => {
        const target = event.target;
        const value = (target.type === 'checkbox' ? target.checked : target.value);

        removeError(name);

        setOnboardingSettings({...onboardingSettings, [name]: value });
    };

    const handleGroup = id => {
        const newChecked = [...groupsChecked]
        const i = newChecked.indexOf(id)

        removeError('groups');

        if (i > -1) {
            newChecked.splice(i, 1)
        } else {
            newChecked.push(id)
        }

        setGroupsChecked(newChecked);
    }

    const handleLevelChildren = (addChildren, ids) => {
        let newChecked = [...groupsChecked];

        removeError('groups');

        if (addChildren) {
            newChecked = [...new Set(newChecked.concat(ids))];
        } else {
            newChecked = newChecked.filter((el) => !ids.includes(el));
        }

        setGroupsChecked(newChecked);
    }

    const postOnboardingSettings = () => {
        setLoading(true);

        api.post('/admin/club/onboarding', {...onboardingSettings, groups: groupsChecked})
            .then((res) => setOnboardingSettings(initOnboardingSettings))
            .catch(error => {
                if (error?.response?.data?.errors) {
                    setErrors(error.response.data.errors)
                }
            }).then(() => setLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field][0];
        return "";
    }

    const removeError = field => {
        const newErrors = {...errors};

        delete newErrors[field];

        setErrors(newErrors);
    }

    return (
        <form noValidate autoComplete="off">
            <Paper className={classes.paper}>
                <Loading isLoading={loading}/>
                <Grid container
                    alignItems="center"
                    justify="space-between"
                    spacing={3}
                    >
                    <Grid item>
                        <Typography variant="h6" component="h2" gutterBottom>
                            Onboarding
                        </Typography>
                    </Grid>
                </Grid>
                <Typography color="textSecondary" gutterBottom>
                    Onboarding allows administrators to force users to view a sequence of screens when they log in before allowing access to the entire app. When you hit the <strong>PUBLISH</strong> button, all members and guests will be forced through the selection of screens you have chosen. If you want to clear the current onboarding sequence, clear all fields and hit the <strong>PUBLISH</strong> button.
                </Typography>
                <Grid container spacing={3}>
                    <Grid item xs={12} className={classes.relative}>
                        <Loading isLoading={(groupsLoading || levelsLoading)}/>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                            <Typography display="inline" color="primary">
                                <strong>Step 1:&nbsp;</strong>
                            </Typography>
                            Onboarding Target Group
                        </Typography>
                        <div className={classes.scroll}>
                            <List className={classes.noContentPadding}>
                                {groups.map(group => (
                                    <ListItem
                                        classes={{ root: classes.listItem }}
                                        button
                                        dense
                                        onClick={() => handleGroup(group.id)}
                                        key={'group' + group.id}
                                        >
                                        <ListItemIcon classes={{ root: classes.listItemIcon }}>
                                            <Checkbox
                                                edge="start"
                                                checked={(groupsChecked.indexOf(group.id) > -1)}
                                                tabIndex={-1}
                                                disableRipple
                                                color="primary"
                                            />
                                        </ListItemIcon>
                                        <ListItemText primary={group.title}/>
                                    </ListItem>
                                ))}
                                {levels.map(level => (
                                    <NestedLevelItem
                                        key={level.id}
                                        level={level}
                                        depth={1}
                                        levelsChecked={groupsChecked}
                                        onCheck={handleGroup}
                                        onCheckChildren={handleLevelChildren}
                                    />
                                ))}
                            </List>
                        </div>
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                            <Typography display="inline" color="primary">
                                <strong>Step 2:&nbsp;</strong>
                            </Typography>
                            Onboarding Message
                        </Typography>
                        <TextField
                            multiline
                            rows="4"
                            rowsMax="4"
                            value={onboardingSettings.onboard_message}
                            onChange={handleChange('onboard_message')}
                            fullWidth
                            error={isError('onboard_message')}
                            helperText={getErrorMessage('onboard_message')}
                            variant="outlined"
                            placeholder="Type a message to display to your users on the first screen."
                            />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                            <Typography display="inline" color="primary">
                                <strong>Step 3:&nbsp;</strong>
                            </Typography>
                            Onboarding Profile
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={onboardingSettings.onboard_profile}
                                    onChange={handleChange("onboard_profile")}
                                    name="onboard_profile"
                                    color="primary"
                                />
                            }
                            label="Display the Update Profile Screen?"
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                            <Typography display="inline" color="primary">
                                <strong>Step 4:&nbsp;</strong>
                            </Typography>
                            Onboarding Password
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={onboardingSettings.onboard_password}
                                    onChange={handleChange("onboard_password")}
                                    name="onboard_password"
                                    color="primary"
                                />
                            }
                            label="Display the Update Password Screen?"
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                            <Typography display="inline" color="primary">
                                <strong>Step 5:&nbsp;</strong>
                            </Typography>
                            Onboarding Membership
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={onboardingSettings.onboard_membership}
                                    onChange={handleChange("onboard_membership")}
                                    name="onboard_membership"
                                    color="primary"
                                />
                            }
                            label="Ask users to participate in the member directory? (Members group only)"
                            margin="normal"
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <Typography variant="subtitle1" color="textSecondary" gutterBottom>
                            <Typography display="inline" color="primary">
                                <strong>Step 6:&nbsp;</strong>
                            </Typography>
                            Onboarding Membership Invoice Payment
                        </Typography>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={onboardingSettings.onboard_membership_invoice}
                                    onChange={handleChange("onboard_membership_invoice")}
                                    name="onboard_membership_invoice"
                                    color="primary"
                                />
                            }
                            label="If member has a membership invoice available, ask them to pay it?"
                            margin="normal"
                        />
                    </Grid>
                </Grid>
                <Typography className={classes.saveButtonContainer}>
                    <SaveButton
                        disabled={loading}
                        variant="contained"
                        color="primary"
                        onClick={postOnboardingSettings}
                        >
                        Publish Onboarding
                    </SaveButton>
                </Typography>
            </Paper>
        </form>
    )
}
